import React, { useEffect, useState, useCallback } from "react";
import { fmt } from "@/lib/api";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Display labels for the underlying status codes. Codes stay stable in the
// data model; the UI uses the controller-friendly labels.
const STATUS_LABEL = {
  expected: "Pending Verification",
  recorded: "Confirmed Posted",
  skipped: "Skipped",
  not_applicable: "Not Applicable",
};
const STATUS_TAG = {
  recorded: "tag ok",
  skipped: "tag idle",
  not_applicable: "tag idle",
  expected: "tag attn",
};
const MATERIALITY_ORDER = { high: 0, medium: 1, low: 2 };

// ─── Period Checklist tab ─────────────────────────────────────────────────────

function ChecklistTab() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/recurring-journals/checklist`, { params: { month } })
      .then(r => { setData(r.data); setLoading(false); });
  }, [month]);

  useEffect(load, [load]);

  async function doRecord(template_id) {
    const erp = window.prompt(
      "Confirm this recurring journal has been posted in the ERP.\n\n" +
      "ERP journal number (required):");
    if (!erp || !erp.trim()) return;
    const amt = window.prompt("Amount posted (optional):");
    const note = window.prompt("Controller notes (optional):") || "";
    await axios.post(`${API}/recurring-journals/record`, {
      template_id, expected_for_month: month,
      erp_journal_number: erp.trim(),
      amount_recorded: amt ? parseFloat(amt) : null,
      controller_notes: note,
    });
    load();
  }
  async function doSkip(template_id) {
    const reason = window.prompt(
      "Skip this recurring journal for this period.\n\n" +
      "Skip reason (required):");
    if (!reason || !reason.trim()) return;
    await axios.post(`${API}/recurring-journals/skip`, {
      template_id, expected_for_month: month, skip_reason: reason.trim(),
    });
    load();
  }
  async function doNotApplicable(template_id) {
    const reason = window.prompt(
      "Mark this recurring journal as Not Applicable for this period.\n\n" +
      "Use this when the underlying business event did not occur (e.g. no " +
      "shareholder interest accrual this month).\n\n" +
      "Reason (required):");
    if (!reason || !reason.trim()) return;
    await axios.post(`${API}/recurring-journals/not-applicable`, {
      template_id, expected_for_month: month, not_applicable_reason: reason.trim(),
    });
    load();
  }

  if (loading || !data) return <div className="loading" data-testid="rj-loading">Loading…</div>;

  const items = [...data.items].sort((a, b) => {
    const m = MATERIALITY_ORDER[a.template.materiality_level] - MATERIALITY_ORDER[b.template.materiality_level];
    if (m !== 0) return m;
    if (a.computed_status === "expected" && b.computed_status !== "expected") return -1;
    if (b.computed_status === "expected" && a.computed_status !== "expected") return 1;
    return a.template.template_code.localeCompare(b.template.template_code);
  });
  const counts = {
    expected: items.filter(i => i.computed_status === "expected").length,
    recorded: items.filter(i => i.computed_status === "recorded").length,
    skipped: items.filter(i => i.computed_status === "skipped").length,
    na: items.filter(i => i.computed_status === "not_applicable").length,
  };

  return (
    <div data-testid="rj-checklist-tab">
      <div className="kpi-strip" style={{ gridTemplateColumns: "repeat(5, 1fr)" }} data-testid="rj-kpis">
        <div className="kpi"><div className="kpi-label">Month</div>
          <select value={month} onChange={e => setMonth(e.target.value)}
                  style={{ fontFamily: "var(--serif)", fontSize: 20, border: "none", background: "transparent" }}
                  data-testid="rj-month-select">
            {["2026-04", "2026-05", "2026-06", "2026-07", "2026-08"].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="kpi"><div className="kpi-label">Pending Verification</div>
          <div className={"kpi-value " + (counts.expected > 0 ? "amber" : "")}>{counts.expected}</div></div>
        <div className="kpi"><div className="kpi-label">Confirmed</div>
          <div className="kpi-value accent">{counts.recorded}</div></div>
        <div className="kpi"><div className="kpi-label">Skipped</div>
          <div className="kpi-value">{counts.skipped}</div></div>
        <div className="kpi"><div className="kpi-label">Not Applicable</div>
          <div className="kpi-value">{counts.na}</div></div>
      </div>

      <div className="worklist-section">
        <div className="worklist-title">
          <div className="heading h2">Checklist · {month}</div>
          <span className="muted" style={{ fontSize: 12 }}>Sorted by materiality, then pending items first</span>
        </div>
        {items.length === 0 && (
          <div className="empty" data-testid="rj-checklist-empty">
            No active templates apply to {month}. Add or activate templates in the Template Library.
          </div>
        )}
        {items.map(i => {
          const t = i.template;
          const cls = STATUS_TAG[i.computed_status] || "tag idle";
          const matClr = t.materiality_level === "high" ? "var(--blocker)" :
                         t.materiality_level === "medium" ? "var(--ink)" : "var(--ink-mute)";
          return (
            <div key={t.id} className="worklist-row"
                 style={{ gridTemplateColumns: "100px 1fr 110px 170px 240px", alignItems: "start" }}
                 data-testid={`rj-${t.template_code}`}>
              <div className="mono" style={{ fontWeight: 600 }}>{t.template_code}</div>
              <div className="primary">
                <div><span className="account">{t.name}</span></div>
                <div className="meta">
                  {t.frequency}
                  {t.owner && <> · Owner {t.owner}</>}
                  {i.log?.erp_journal_number && <> · ERP {i.log.erp_journal_number}</>}
                </div>
                {t.lines && t.lines.length > 0 && (
                  <ExpectedJournalLines lines={t.lines} testid={`rj-lines-${t.template_code}`} />
                )}
              </div>
              <div style={{ color: matClr, fontSize: 11, letterSpacing: "0.06em",
                            textTransform: "uppercase", fontWeight: 700 }}>
                {t.materiality_level}
              </div>
              <div><span className={cls} data-testid={`rj-status-${t.template_code}`}>
                {STATUS_LABEL[i.computed_status] || i.computed_status}
              </span></div>
              <div style={{ textAlign: "right" }}>
                {i.computed_status === "expected" && i.applicable && (
                  <div style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button className="btn primary btn-sm" onClick={() => doRecord(t.id)}
                            data-testid={`rj-record-${t.template_code}`}>Confirm posted</button>
                    <button className="btn btn-sm" onClick={() => doSkip(t.id)}
                            data-testid={`rj-skip-${t.template_code}`}>Skip</button>
                    <button className="btn btn-sm" onClick={() => doNotApplicable(t.id)}
                            data-testid={`rj-na-${t.template_code}`}>Not applicable</button>
                  </div>
                )}
                {i.computed_status === "recorded" && i.log?.recorded_at && (
                  <span className="muted" style={{ fontSize: 11 }}>
                    {fmt.date(i.log.recorded_at)} · {i.log.recorded_by}
                  </span>
                )}
                {i.computed_status === "skipped" && i.log?.skip_reason && (
                  <span className="muted" style={{ fontSize: 11 }}>{i.log.skip_reason}</span>
                )}
                {i.computed_status === "not_applicable" && i.log?.not_applicable_reason && (
                  <span className="muted" style={{ fontSize: 11 }}>{i.log.not_applicable_reason}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="muted" style={{ marginTop: 32, fontSize: 12, padding: 12,
            background: "var(--surface-elev)", border: "1px solid var(--line)", borderRadius: 6 }}>
        Month-End Commander records the controller&apos;s confirmation that each recurring
        General Ledger adjustment has been posted in the ERP. No accounting entries are
        created here. Outstanding recurring items never block month-end completion.
      </div>
    </div>
  );
}

// ─── Template Library tab ────────────────────────────────────────────────────

const EMPTY_TEMPLATE = {
  name: "",
  category: "A",
  description: "",
  frequency: "monthly",
  schedule_day_of_period: "last_day",
  materiality_level: "medium",
  lines: [],
  effective_from: "",
  effective_to: "",
  owner: "",
  auto_reverse: false,
};

// Compute totals + balance from the lines array (used both by the editor
// and the read-only checklist summaries).
function lineTotals(lines) {
  const dr = (lines || [])
    .filter(l => l.kind === "debit")
    .reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const cr = (lines || [])
    .filter(l => l.kind === "credit")
    .reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  return {
    debits: dr,
    credits: cr,
    balanced: Math.abs(dr - cr) < 0.01 && dr > 0,
  };
}

function TemplateLibraryTab() {
  const [templates, setTemplates] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);  // null | "new" | template object

  const load = useCallback(() => {
    axios.get(`${API}/recurring-journals/templates`).then(r => setTemplates(r.data));
  }, []);
  useEffect(load, [load]);

  async function patchTemplate(id, body) {
    await axios.patch(`${API}/recurring-journals/templates/${id}`, body);
    load();
  }
  async function retireTemplate(id) {
    if (!window.confirm("Retire this template? It will be deactivated and will no longer appear on any future period checklist. Historical confirmations are preserved.")) return;
    await axios.post(`${API}/recurring-journals/templates/${id}/retire`);
    load();
  }
  async function cloneTemplate(id) {
    if (!window.confirm("Clone this template? A new copy will be created with a fresh code and a [CLONE] prefix on the name.")) return;
    await axios.post(`${API}/recurring-journals/templates/${id}/clone`);
    load();
  }

  if (templates === null) return <div className="loading" data-testid="rj-lib-loading">Loading…</div>;

  const visible = templates
    .filter(t => showInactive || t.active)
    .sort((a, b) => a.template_code.localeCompare(b.template_code));

  return (
    <div data-testid="rj-library-tab">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div className="heading h2">Template Library</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Reusable recurring General Ledger adjustment templates.
            Each template defines an expectation only — no accounting entries are generated here.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label style={{ fontSize: 12, color: "var(--ink-mute)", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
                   data-testid="rj-toggle-inactive" />
            Show retired / inactive
          </label>
          <button className="btn primary" onClick={() => setEditing("new")}
                  data-testid="rj-new-template">New template</button>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="empty" data-testid="rj-library-empty">
          No templates yet. Click <strong>New template</strong> to define the first recurring General Ledger adjustment expectation.
        </div>
      ) : (
        <div style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", background: "var(--surface)" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "110px 1fr 130px 110px 130px 90px 220px",
            columnGap: 16,
            padding: "10px 14px",
            background: "var(--surface-elev)",
            borderBottom: "1px solid var(--line)",
            fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
            fontWeight: 600, color: "var(--ink-mute)",
          }}>
            <div>Code</div>
            <div>Name</div>
            <div>Frequency</div>
            <div>Materiality</div>
            <div>Owner</div>
            <div>State</div>
            <div style={{ textAlign: "right" }}>Actions</div>
          </div>
          {visible.map(t => (
            <div key={t.id}
                 data-testid={`rj-tpl-row-${t.template_code}`}
                 style={{
                   display: "grid",
                   gridTemplateColumns: "110px 1fr 130px 110px 130px 90px 220px",
                   columnGap: 16,
                   padding: "12px 14px",
                   borderBottom: "1px solid var(--line)",
                   alignItems: "center",
                   fontSize: 13,
                 }}>
              <div className="mono" style={{ fontWeight: 600 }}>{t.template_code}</div>
              <div>
                <div style={{ fontWeight: 600 }}>{t.name}</div>
                {t.description && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{t.description}</div>}
                {(t.lines && t.lines.length > 0) && (
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {t.lines.length} line{t.lines.length === 1 ? "" : "s"}
                    {" · "}
                    ${fmt.currency(lineTotals(t.lines).debits)} balanced
                  </div>
                )}
              </div>
              <div>{t.frequency}</div>
              <div style={{
                color: t.materiality_level === "high" ? "var(--blocker)" :
                       t.materiality_level === "medium" ? "var(--ink)" : "var(--ink-mute)",
                fontSize: 11, letterSpacing: "0.06em",
                textTransform: "uppercase", fontWeight: 700,
              }}>{t.materiality_level}</div>
              <div>{t.owner || <span className="muted">—</span>}</div>
              <div>
                {t.active
                  ? <span className="tag ok" data-testid={`rj-tpl-active-${t.template_code}`}>Active</span>
                  : <span className="tag idle" data-testid={`rj-tpl-inactive-${t.template_code}`}>
                      {t.effective_to ? "Retired" : "Inactive"}
                    </span>}
              </div>
              <div style={{ textAlign: "right", display: "inline-flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <button className="btn btn-sm" onClick={() => setEditing(t)}
                        data-testid={`rj-tpl-edit-${t.template_code}`}>Edit</button>
                {t.active
                  ? <button className="btn btn-sm" onClick={() => patchTemplate(t.id, { active: false })}
                            data-testid={`rj-tpl-deactivate-${t.template_code}`}>Deactivate</button>
                  : <button className="btn btn-sm" onClick={() => patchTemplate(t.id, { active: true })}
                            data-testid={`rj-tpl-activate-${t.template_code}`}>Activate</button>}
                <button className="btn btn-sm" onClick={() => cloneTemplate(t.id)}
                        data-testid={`rj-tpl-clone-${t.template_code}`}>Clone</button>
                {t.active && (
                  <button className="btn btn-sm" onClick={() => retireTemplate(t.id)}
                          data-testid={`rj-tpl-retire-${t.template_code}`}>Retire</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateEditor
          template={editing === "new" ? EMPTY_TEMPLATE : editing}
          isNew={editing === "new"}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Template editor modal ───────────────────────────────────────────────────

function TemplateEditor({ template, isNew, onCancel, onSaved }) {
  const [t, setT] = useState(() => ({
    ...EMPTY_TEMPLATE,
    ...template,
    lines: (template?.lines || []).map((l, i) => ({ ...l, order: i })),
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setT(prev => ({ ...prev, [k]: v }));
  const setLine = (idx, patch) => setT(prev => ({
    ...prev,
    lines: prev.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));
  const addLine = (kind) => setT(prev => ({
    ...prev,
    lines: [...prev.lines, { kind, gl_account: "", description: "", amount: "", order: prev.lines.length }],
  }));
  const removeLine = (idx) => setT(prev => ({
    ...prev,
    lines: prev.lines.filter((_, i) => i !== idx).map((l, i) => ({ ...l, order: i })),
  }));
  const totals = lineTotals(t.lines);
  const debitCount = t.lines.filter(l => l.kind === "debit").length;
  const creditCount = t.lines.filter(l => l.kind === "credit").length;

  function validateClient() {
    if (!t.name || !t.name.trim()) return "Name is required.";
    if (t.lines.length === 0) return "Add at least one debit and one credit line.";
    if (debitCount === 0) return "At least one debit line is required.";
    if (creditCount === 0) return "At least one credit line is required.";
    for (let i = 0; i < t.lines.length; i++) {
      const l = t.lines[i];
      if (!l.gl_account || !l.gl_account.trim()) return `Line ${i + 1}: GL account is required.`;
      const amt = parseFloat(l.amount);
      if (!amt || amt <= 0) return `Line ${i + 1}: amount must be greater than zero.`;
    }
    if (!totals.balanced) {
      return `Template is out of balance: debits $${totals.debits.toFixed(2)} vs credits $${totals.credits.toFixed(2)}.`;
    }
    return null;
  }

  async function save() {
    const clientErr = validateClient();
    if (clientErr) { setErr(clientErr); return; }
    setSaving(true);
    setErr(null);
    try {
      const body = {
        name: t.name,
        category: t.category,
        description: t.description,
        frequency: t.frequency,
        schedule_day_of_period: t.schedule_day_of_period,
        materiality_level: t.materiality_level,
        lines: t.lines.map((l, i) => ({
          kind: l.kind,
          gl_account: (l.gl_account || "").trim(),
          description: l.description || "",
          amount: parseFloat(l.amount),
          order: i,
        })),
        effective_from: t.effective_from || null,
        effective_to: t.effective_to || null,
        owner: t.owner,
        auto_reverse: !!t.auto_reverse,
      };
      if (isNew) {
        await axios.post(`${API}/recurring-journals/templates`, body);
      } else {
        await axios.patch(`${API}/recurring-journals/templates/${template.id}`, body);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      data-testid="rj-template-editor"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,17,21,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60,
      }}
      onClick={onCancel}
    >
      <div onClick={e => e.stopPropagation()}
           style={{
             background: "var(--surface)", borderRadius: 10,
             padding: 24, width: 820, maxHeight: "92vh", overflowY: "auto",
             boxShadow: "0 12px 32px rgba(15,17,21,0.18)",
           }}>
        <div className="heading h2" style={{ marginBottom: 4 }}>
          {isNew ? "New recurring template" : `Edit ${template.template_code}`}
        </div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 18 }}>
          Templates define the expected journal structure only — they never generate accounting entries.
        </div>

        <Grid2>
          <Field label="Name *">
            <input className="input" value={t.name}
                   onChange={e => set("name", e.target.value)}
                   data-testid="rj-edit-name" />
          </Field>
          <Field label="Responsible owner">
            <input className="input" value={t.owner || ""} placeholder="e.g. Senior Accountant"
                   onChange={e => set("owner", e.target.value)}
                   data-testid="rj-edit-owner" />
          </Field>
        </Grid2>

        <Field label="Description">
          <textarea className="input" rows={2} value={t.description || ""}
                    onChange={e => set("description", e.target.value)}
                    data-testid="rj-edit-description" />
        </Field>

        <Grid2>
          <Field label="Frequency">
            <select className="input" value={t.frequency}
                    onChange={e => set("frequency", e.target.value)}
                    data-testid="rj-edit-frequency">
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="semi_annual">Semi-annual</option>
              <option value="annual">Annual</option>
            </select>
          </Field>
          <Field label="Expected posting timing">
            <select className="input" value={t.schedule_day_of_period}
                    onChange={e => set("schedule_day_of_period", e.target.value)}
                    data-testid="rj-edit-timing">
              <option value="last_day">Last day of period</option>
              <option value="day_1">Day 1</option>
              <option value="day_15">Day 15</option>
              <option value="day_30">Day 30</option>
            </select>
          </Field>
        </Grid2>

        <Grid2>
          <Field label="Materiality">
            <select className="input" value={t.materiality_level}
                    onChange={e => set("materiality_level", e.target.value)}
                    data-testid="rj-edit-materiality">
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </Field>
          <Field label="Category">
            <select className="input" value={t.category}
                    onChange={e => set("category", e.target.value)}
                    data-testid="rj-edit-category">
              <option value="A">A — True recurring</option>
              <option value="B">B — Variable recurring</option>
            </select>
          </Field>
        </Grid2>

        {/* Journal Lines — the expected accounting structure */}
        <div style={{ marginTop: 8, marginBottom: 16,
                      border: "1px solid var(--line)", borderRadius: 8, padding: 14 }}
             data-testid="rj-edit-lines-section">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Journal lines</div>
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                Add as many debit and credit lines as the recurring journal requires.
                Debits and credits must balance.
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button type="button" className="btn btn-sm" onClick={() => addLine("debit")}
                      data-testid="rj-edit-add-debit">+ Debit line</button>
              <button type="button" className="btn btn-sm" onClick={() => addLine("credit")}
                      data-testid="rj-edit-add-credit">+ Credit line</button>
            </div>
          </div>

          {t.lines.length === 0 ? (
            <div className="empty" style={{ fontSize: 12, padding: "16px 0" }}
                 data-testid="rj-edit-lines-empty">
              No lines yet. Add at least one debit line and one credit line.
            </div>
          ) : (
            <div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "60px 1fr 1.4fr 130px 36px",
                columnGap: 8,
                fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em",
                fontWeight: 700, color: "var(--ink-mute)",
                paddingBottom: 6, borderBottom: "1px solid var(--line)",
              }}>
                <div>Type</div>
                <div>GL account *</div>
                <div>Line description</div>
                <div style={{ textAlign: "right" }}>Amount *</div>
                <div></div>
              </div>
              {t.lines.map((l, idx) => (
                <div key={idx}
                     data-testid={`rj-edit-line-${idx}`}
                     style={{
                       display: "grid",
                       gridTemplateColumns: "60px 1fr 1.4fr 130px 36px",
                       columnGap: 8,
                       padding: "8px 0",
                       borderBottom: "1px solid var(--line)",
                       alignItems: "center",
                     }}>
                  <select className="input" value={l.kind}
                          onChange={e => setLine(idx, { kind: e.target.value })}
                          data-testid={`rj-edit-line-${idx}-kind`}
                          style={{
                            fontWeight: 700,
                            color: l.kind === "debit" ? "var(--accent)" : "var(--ink)",
                          }}>
                    <option value="debit">DR</option>
                    <option value="credit">CR</option>
                  </select>
                  <input className="input" value={l.gl_account || ""}
                         placeholder="e.g. 5300-Depreciation"
                         onChange={e => setLine(idx, { gl_account: e.target.value })}
                         data-testid={`rj-edit-line-${idx}-account`} />
                  <input className="input" value={l.description || ""}
                         placeholder="optional"
                         onChange={e => setLine(idx, { description: e.target.value })}
                         data-testid={`rj-edit-line-${idx}-desc`} />
                  <input className="input" type="number" step="0.01" min="0"
                         value={l.amount === "" || l.amount == null ? "" : l.amount}
                         placeholder="0.00"
                         onChange={e => setLine(idx, { amount: e.target.value })}
                         data-testid={`rj-edit-line-${idx}-amount`}
                         style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }} />
                  <button type="button"
                          onClick={() => removeLine(idx)}
                          aria-label="Delete line"
                          title="Delete line"
                          data-testid={`rj-edit-line-${idx}-delete`}
                          style={{
                            background: "transparent", border: "none",
                            cursor: "pointer", fontSize: 18, color: "var(--ink-mute)",
                            padding: 0,
                          }}>×</button>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end",
                            alignItems: "center", gap: 18, padding: "10px 0 0 0",
                            fontSize: 12 }}
                   data-testid="rj-edit-totals">
                <span>Debits&nbsp;<strong className="mono">${fmt.currency(totals.debits)}</strong></span>
                <span>Credits&nbsp;<strong className="mono">${fmt.currency(totals.credits)}</strong></span>
                <span style={{
                  color: totals.balanced ? "var(--accent)" : "var(--blocker)",
                  fontWeight: 700,
                }} data-testid="rj-edit-balance-status">
                  {totals.balanced ? "Balanced ✓" : "Out of balance ✕"}
                </span>
              </div>
            </div>
          )}
        </div>

        <Grid2>
          <Field label="Effective from (YYYY-MM)">
            <input className="input" placeholder="e.g. 2026-01" value={t.effective_from || ""}
                   onChange={e => set("effective_from", e.target.value)}
                   data-testid="rj-edit-eff-from" />
          </Field>
          <Field label="Effective to (YYYY-MM)">
            <input className="input" placeholder="leave blank for open-ended" value={t.effective_to || ""}
                   onChange={e => set("effective_to", e.target.value)}
                   data-testid="rj-edit-eff-to" />
          </Field>
        </Grid2>

        {err && <div style={{ color: "var(--blocker)", fontSize: 12, marginTop: 8 }} data-testid="rj-edit-error">{err}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button className="btn" onClick={onCancel} disabled={saving}
                  data-testid="rj-edit-cancel">Cancel</button>
          <button className="btn primary" onClick={save}
                  disabled={saving || !!validateClient()}
                  data-testid="rj-edit-save">{saving ? "Saving…" : (isNew ? "Create template" : "Save changes")}</button>
        </div>
      </div>
    </div>
  );
}

const Grid2 = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>{children}</div>
);

const Field = ({ label, children }) => (
  <label style={{ display: "block", marginBottom: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-mute)",
                  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
    {children}
  </label>
);

// Read-only renderer for the expected journal structure on the Period
// Checklist. Mirrors how a controller reads a journal: DR lines first,
// then CR lines, indented and right-aligned amounts.
function ExpectedJournalLines({ lines, testid }) {
  if (!lines || lines.length === 0) return null;
  const ordered = [
    ...lines.filter(l => l.kind === "debit"),
    ...lines.filter(l => l.kind === "credit"),
  ];
  const totals = lineTotals(lines);
  return (
    <div data-testid={testid}
         style={{ marginTop: 10, padding: "10px 12px",
                  background: "var(--surface-elev)", border: "1px solid var(--line)",
                  borderRadius: 6, fontSize: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                    textTransform: "uppercase", color: "var(--ink-mute)", marginBottom: 6 }}>
        Expected journal
      </div>
      {ordered.map((l, idx) => (
        <div key={idx} style={{ display: "grid",
              gridTemplateColumns: "44px 1fr auto", columnGap: 8,
              padding: "3px 0", color: "var(--ink)" }}>
          <span style={{ fontWeight: 700,
                         color: l.kind === "debit" ? "var(--accent)" : "var(--ink)" }}>
            {l.kind === "debit" ? "DR" : "CR"}
          </span>
          <span>
            {l.gl_account}
            {l.description && <span className="muted" style={{ fontSize: 11 }}> · {l.description}</span>}
          </span>
          <span className="mono">${fmt.currency(l.amount)}</span>
        </div>
      ))}
      <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--line)",
                    display: "flex", justifyContent: "space-between", fontSize: 11,
                    color: "var(--ink-mute)" }}>
        <span>
          DR ${fmt.currency(totals.debits)} · CR ${fmt.currency(totals.credits)}
        </span>
        <span style={{ color: totals.balanced ? "var(--accent)" : "var(--blocker)", fontWeight: 700 }}>
          {totals.balanced ? "Balanced ✓" : "Out of balance ✕"}
        </span>
      </div>
    </div>
  );
}

// ─── Outer page (tab shell) ──────────────────────────────────────────────────

export default function RecurringJournals() {
  const [tab, setTab] = useState("checklist");
  return (
    <div data-testid="recurring-journals-page">
      <header style={{ marginBottom: 18 }}>
        <div className="heading h1">Recurring Journals</div>
        <div className="muted" style={{ marginTop: 4, fontSize: 14 }}>
          Control register for recurring General Ledger adjustments — depreciation, shareholder interest,
          prepaid allocations, accruals. Records the controller&apos;s monthly confirmation that each
          expected adjustment has been posted in the ERP. No accounting entries are created here.
        </div>
      </header>

      <div role="tablist" data-testid="rj-tabs"
           style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--line)", marginBottom: 24 }}>
        <Tab id="checklist" active={tab === "checklist"} onClick={() => setTab("checklist")}>
          Period Checklist
        </Tab>
        <Tab id="library" active={tab === "library"} onClick={() => setTab("library")}>
          Template Library
        </Tab>
      </div>

      {tab === "checklist" && <ChecklistTab />}
      {tab === "library" && <TemplateLibraryTab />}
    </div>
  );
}

const Tab = ({ id, active, onClick, children }) => (
  <button
    role="tab"
    aria-selected={active}
    data-testid={`rj-tab-${id}`}
    onClick={onClick}
    style={{
      background: "transparent", border: "none",
      padding: "10px 18px", cursor: "pointer",
      fontSize: 13, fontWeight: 600,
      color: active ? "var(--accent)" : "var(--ink-mute)",
      borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
      marginBottom: -1,
      letterSpacing: "0.02em",
    }}
  >
    {children}
  </button>
);
